// Result-share module: aggregates event detail into a summary, paints a
// shareable card onto a canvas, and exposes a sheet to preview/share/save it.
//
// `computeShareSummary` is pure (no DOM, no state) — it ranks teams or
// players by the multi-key win-rate sort used elsewhere, picks the team-mode
// MVP, and returns a uniform shape across regular/individual/team events.
//
// `drawShareCanvas` paints a section list onto a 2x-DPR canvas. Sections are
// `{ height, draw(ctx, x, y) }` blocks stacked vertically inside a rounded
// gradient card; the layout pre-measures so the canvas height is known
// before painting.

import { useEffect, useRef, useState } from "preact/hooks";
import { state } from "../state";
import { Icon } from "../ui/components/Icon";
import { mountPreact } from "../ui/preact-mount";
import { closeSheet, toast, openSheet } from "../ui/sheet";
import {
  shareFont,
  ellipsizeText,
  wrapTextLines,
  roundRectPath,
  loadImageAsync,
} from "../ui/canvas";
import { formatDateKo, eventKindLabel } from "../ui/format";
import { matchPlayerName } from "../matches";

export function computeShareSummary(detail: any): any {
  const ev = detail.event || {};
  const participants = detail.participants || [];
  const matches = detail.matches || [];
  const teams = detail.teams || [];
  const decided = matches.filter((m: any) => m.winner_side);
  const isTeam = ev.mode === "team";
  const isSpecial = ev.event_type === "special";

  const stats = new Map<string, any>();
  const labelOf = new Map<string, string>();
  const playerStats = new Map<string, any>();

  if (isTeam) {
    for (const t of teams) {
      stats.set(t.team_id, { wins: 0, losses: 0, draws: 0, scored: 0, conceded: 0 });
      labelOf.set(t.team_id, t.team_name || `팀 ${t.team_order || ""}`);
    }
  } else {
    for (const p of participants) {
      stats.set(p.event_participant_id, {
        wins: 0,
        losses: 0,
        draws: 0,
        scored: 0,
        conceded: 0,
      });
      labelOf.set(p.event_participant_id, matchPlayerName(p));
    }
  }
  for (const p of participants) {
    playerStats.set(p.event_participant_id, {
      participant: p,
      wins: 0,
      losses: 0,
      draws: 0,
      scored: 0,
      conceded: 0,
    });
  }

  for (const m of decided) {
    const sideA = (m.sides || []).find((s: any) => s.side_number === 1);
    const sideB = (m.sides || []).find((s: any) => s.side_number === 2);
    if (!sideA || !sideB) continue;
    const scoreA = Number(m.side_a_score) || 0;
    const scoreB = Number(m.side_b_score) || 0;
    const keysA = isTeam ? (sideA.team_id ? [sideA.team_id] : []) : sideA.player_ids || [];
    const keysB = isTeam ? (sideB.team_id ? [sideB.team_id] : []) : sideB.player_ids || [];
    const pidsA = sideA.player_ids || [];
    const pidsB = sideB.player_ids || [];
    const bump = (map: Map<string, any>, keys: string[], field: string) =>
      keys.forEach((k) => {
        if (map.has(k)) map.get(k)[field]++;
      });
    const addScore = (map: Map<string, any>, keys: string[], s: number, c: number) =>
      keys.forEach((k) => {
        if (map.has(k)) {
          map.get(k).scored += s;
          map.get(k).conceded += c;
        }
      });
    // 팀전은 서로 다른 팀 간 경기(정상 팀전)만 MVP 후보 개인기록에 반영.
    // 내부 매치(같은 팀끼리)·cross_team_mixed(team_id=None)는 제외.
    const mvpEligible =
      !isTeam || (keysA.length > 0 && keysB.length > 0 && keysA[0] !== keysB[0]);
    if (m.winner_side === "side_a") {
      bump(stats, keysA, "wins");
      bump(stats, keysB, "losses");
      if (mvpEligible) {
        bump(playerStats, pidsA, "wins");
        bump(playerStats, pidsB, "losses");
      }
    } else if (m.winner_side === "side_b") {
      bump(stats, keysB, "wins");
      bump(stats, keysA, "losses");
      if (mvpEligible) {
        bump(playerStats, pidsB, "wins");
        bump(playerStats, pidsA, "losses");
      }
    } else if (m.winner_side === "draw") {
      bump(stats, keysA, "draws");
      bump(stats, keysB, "draws");
      if (mvpEligible) {
        bump(playerStats, pidsA, "draws");
        bump(playerStats, pidsB, "draws");
      }
    }
    addScore(stats, keysA, scoreA, scoreB);
    addScore(stats, keysB, scoreB, scoreA);
    if (mvpEligible) {
      addScore(playerStats, pidsA, scoreA, scoreB);
      addScore(playerStats, pidsB, scoreB, scoreA);
    }
  }

  const rows = [...stats.entries()]
    .map(([key, s]) => ({
      key,
      name: labelOf.get(key) || "-",
      ...s,
      total: s.wins + s.losses + s.draws,
      decided: s.wins + s.losses,
      diff: s.scored - s.conceded,
    }))
    .filter((r: any) => r.total > 0)
    .sort((a: any, b: any) => {
      const ra = a.decided ? a.wins / a.decided : 0;
      const rb = b.decided ? b.wins / b.decided : 0;
      if (rb !== ra) return rb - ra;
      if (b.wins !== a.wins) return b.wins - a.wins;
      if (b.diff !== a.diff) return b.diff - a.diff;
      return b.total - a.total;
    });

  const summary: any = {
    kind: isSpecial && isTeam ? "team" : isSpecial ? "individual" : "regular",
    participantCount: participants.length,
    matchCount: matches.filter((m: any) => m.result_status !== "skipped").length,
    decidedCount: decided.length,
    rounds: new Set(
      matches.filter((m: any) => m.result_status !== "skipped").map((m: any) => m.round_number),
    ).size,
    top: rows.slice(0, 3),
    mvp: null,
    winnerTeam: null,
    runnerUpTeam: null,
  };

  if (isTeam && rows.length > 0 && !ev.is_friendly_match) {
    summary.winnerTeam = rows[0];
    summary.runnerUpTeam = rows[1] || null;
    if (ev.status === "completed") {
      const winningKey = rows[0].key;
      const winningTeam = teams.find((t: any) => t.team_id === winningKey);
      const candidatePids = winningTeam ? winningTeam.participant_ids || [] : [];
      const mvpCandidates = candidatePids
        .map((pid: string) => playerStats.get(pid))
        .filter((ps: any) => ps && ps.wins + ps.losses + ps.draws > 0)
        .sort((a: any, b: any) => {
          const ad = a.wins + a.losses;
          const bd = b.wins + b.losses;
          const ra = ad ? a.wins / ad : 0;
          const rb = bd ? b.wins / bd : 0;
          if (rb !== ra) return rb - ra;
          if (b.wins !== a.wins) return b.wins - a.wins;
          return b.scored - b.conceded - (a.scored - a.conceded);
        });
      summary.mvp = mvpCandidates[0] || null;
    }
  }
  return summary;
}

export async function drawShareCanvas(
  detail: any,
  summary: any,
  options: { showAll?: boolean } = {},
): Promise<HTMLCanvasElement> {
  const showAll = !!options.showAll;
  const ev = detail.event || {};
  const club = state.club || {};

  // 공유 이미지는 카톡/SNS에서 확대해서 보기도 하므로 디바이스 픽셀 비율과
  // 무관하게 3x로 렌더 — 텍스트·로고가 또렷하게 나옴. 메모리 영향 작음
  // (대략 W=388, H=1500, 3x → 4.2M 픽셀, ~17MB).
  const DPR = 3;
  const W = 388; // logical width of the exported image
  const PAD_TOP = 56; // room for iOS status bar / share UI
  const PAD_SIDE = 14;
  const PAD_BOT = 14;
  const CARD_W = W - PAD_SIDE * 2;
  const CARD_PAD_X = 22;
  const CARD_PAD_Y = 22;
  const INNER_W = CARD_W - CARD_PAD_X * 2;

  // Ensure fonts are loaded before we measure anything.
  if (document.fonts && document.fonts.load) {
    try {
      await Promise.all([
        document.fonts.load(`800 22px Pretendard`),
        document.fonts.load(`700 16px Pretendard`),
        document.fonts.load(`500 13px Pretendard`),
      ]);
      if (document.fonts.ready) await document.fonts.ready;
    } catch {
      /* ignore */
    }
  }

  const logoImg = club.logo_url ? await loadImageAsync(club.logo_url) : null;

  // Measurement context (any canvas works).
  const meas = document.createElement("canvas").getContext("2d") as CanvasRenderingContext2D;

  type Section = { height: number; draw: (ctx: CanvasRenderingContext2D, x: number, y: number) => void };
  const sections: Section[] = [];

  // ---- Header: logo + club name + "경기 결과" eyebrow ----
  const LOGO_SIZE = 36;
  const HEADER_TEXT_X = logoImg ? LOGO_SIZE + 10 : 0;
  sections.push({
    height: Math.max(LOGO_SIZE, 44),
    draw(ctx, x, y) {
      if (logoImg) {
        // Circular clip for the logo.
        ctx.save();
        ctx.beginPath();
        ctx.arc(x + LOGO_SIZE / 2, y + LOGO_SIZE / 2, LOGO_SIZE / 2, 0, Math.PI * 2);
        ctx.closePath();
        ctx.clip();
        ctx.drawImage(logoImg, x, y, LOGO_SIZE, LOGO_SIZE);
        ctx.restore();
      }
      ctx.fillStyle = "#fff";
      ctx.font = shareFont(700, 15);
      ctx.textBaseline = "top";
      ctx.fillText(
        ellipsizeText(((ctx.font = shareFont(700, 15)), ctx), club.name || "테니스 클럽", INNER_W - HEADER_TEXT_X),
        x + HEADER_TEXT_X,
        y + 2,
      );
      ctx.fillStyle = "rgba(255,255,255,0.72)";
      ctx.font = shareFont(500, 12);
      ctx.fillText("경기 결과", x + HEADER_TEXT_X, y + 22);
    },
  });

  // spacer
  sections.push({ height: 18, draw() {} });

  // ---- Title (event name) ----
  meas.font = shareFont(800, 22);
  const titleLines = wrapTextLines(meas, ev.title || "일정", INNER_W, 3);
  const TITLE_LH = 30;
  sections.push({
    height: titleLines.length * TITLE_LH,
    draw(ctx, x, y) {
      ctx.fillStyle = "#fff";
      ctx.font = shareFont(800, 22);
      ctx.textBaseline = "top";
      titleLines.forEach((ln, i) => {
        ctx.fillText(ln, x, y + i * TITLE_LH);
      });
    },
  });

  // ---- Meta line (date · location · kind · vs opponent) ----
  const metaParts = [formatDateKo(ev.event_date), ev.location || "", eventKindLabel(ev)];
  if (ev.is_friendly_match && ev.opponent_club_name) metaParts.push(`vs ${ev.opponent_club_name}`);
  const metaText = metaParts.filter(Boolean).join(" · ");
  meas.font = shareFont(500, 13);
  const metaLines = wrapTextLines(meas, metaText, INNER_W, 2);
  const META_LH = 20;
  sections.push({ height: 4, draw() {} });
  sections.push({
    height: metaLines.length * META_LH,
    draw(ctx, x, y) {
      ctx.fillStyle = "rgba(255,255,255,0.78)";
      ctx.font = shareFont(500, 13);
      ctx.textBaseline = "top";
      metaLines.forEach((ln, i) => {
        ctx.fillText(ln, x, y + i * META_LH);
      });
    },
  });

  sections.push({ height: 20, draw() {} });

  // ---- Body by kind ----
  if (summary.kind === "team" && summary.winnerTeam) {
    const w = summary.winnerTeam;
    meas.font = shareFont(800, 26);
    const nameLines = wrapTextLines(meas, w.name, INNER_W - 24, 2);
    const wRecord =
      w.draws > 0 ? `${w.wins}승 ${w.draws}무 ${w.losses}패` : `${w.wins}승 ${w.losses}패`;
    const sub = `${wRecord} · 득실 ${w.diff >= 0 ? "+" : ""}${w.diff}`;
    const winnerBoxH = 18 + 22 + nameLines.length * 32 + 6 + 20 + 18;
    sections.push({
      height: winnerBoxH,
      draw(ctx, x, y) {
        // Winner panel with soft bg.
        ctx.fillStyle = "rgba(255,255,255,0.1)";
        roundRectPath(ctx, x, y, INNER_W, winnerBoxH, 14);
        ctx.fill();
        const innerX = x + 14;
        let cy = y + 14;
        ctx.fillStyle = "rgba(255,255,255,0.82)";
        ctx.font = shareFont(600, 13);
        ctx.textBaseline = "top";
        ctx.fillText("🏆 우승", innerX, cy);
        cy += 22;
        ctx.fillStyle = "#fde68a";
        ctx.font = shareFont(800, 26);
        nameLines.forEach((ln) => {
          ctx.fillText(ln, innerX, cy);
          cy += 32;
        });
        cy += 2;
        ctx.fillStyle = "rgba(255,255,255,0.78)";
        ctx.font = shareFont(500, 13);
        ctx.fillText(
          ellipsizeText(((ctx.font = shareFont(500, 13)), ctx), sub, INNER_W - 28),
          innerX,
          cy,
        );
      },
    });

    // Runner-up + MVP row
    const cells: any[] = [];
    if (summary.runnerUpTeam) {
      cells.push({ label: "🥈 준우승", value: summary.runnerUpTeam.name, sub: "" });
    }
    if (summary.mvp) {
      const mv = summary.mvp;
      const mvpDecided = mv.wins + mv.losses;
      const mvpRateStr = mvpDecided > 0 ? `${Math.round((mv.wins / mvpDecided) * 100)}%` : "-";
      const mvpRecord =
        mv.draws > 0
          ? `${mv.wins}승 ${mv.draws}무 ${mv.losses}패`
          : `${mv.wins}승 ${mv.losses}패`;
      cells.push({
        label: "⭐ MVP",
        value: matchPlayerName(mv.participant),
        sub: `${mvpRecord} · 승률 ${mvpRateStr}`,
      });
    }
    if (cells.length) {
      sections.push({ height: 12, draw() {} });
      const gap = 10;
      const cellW = (INNER_W - gap * (cells.length - 1)) / cells.length;
      sections.push({
        height: 78,
        draw(ctx, x, y) {
          cells.forEach((c, i) => {
            const cx = x + i * (cellW + gap);
            ctx.fillStyle = "rgba(255,255,255,0.08)";
            roundRectPath(ctx, cx, y, cellW, 78, 12);
            ctx.fill();
            ctx.fillStyle = "rgba(255,255,255,0.72)";
            ctx.font = shareFont(600, 12);
            ctx.textBaseline = "top";
            ctx.fillText(c.label, cx + 12, y + 12);
            ctx.fillStyle = "#fff";
            ctx.font = shareFont(700, 16);
            const valLine = ellipsizeText(ctx, c.value, cellW - 24);
            ctx.fillText(valLine, cx + 12, y + 30);
            if (c.sub) {
              ctx.fillStyle = "rgba(255,255,255,0.72)";
              ctx.font = shareFont(500, 12);
              ctx.fillText(ellipsizeText(ctx, c.sub, cellW - 24), cx + 12, y + 52);
            }
          });
        },
      });
    }
  } else if (summary.kind === "individual") {
    sections.push({
      height: 22,
      draw(ctx, x, y) {
        ctx.fillStyle = "rgba(255,255,255,0.82)";
        ctx.font = shareFont(700, 14);
        ctx.textBaseline = "top";
        ctx.fillText("🏆 당일 순위", x, y);
      },
    });
    sections.push({ height: 10, draw() {} });
    const medals = ["🥇", "🥈", "🥉"];
    const rows = summary.top.slice(0, 3);
    rows.forEach((r: any, i: number) => {
      const dec = r.decided != null ? r.decided : r.wins + r.losses;
      const rateStr = dec > 0 ? `${Math.round((r.wins / dec) * 100)}%` : "-";
      const recordStr =
        r.draws > 0
          ? `${r.wins}승 ${r.draws}무 ${r.losses}패`
          : `${r.wins}승 ${r.losses}패`;
      sections.push({
        height: 44,
        draw(ctx, x, y) {
          ctx.fillStyle = "rgba(255,255,255,0.08)";
          roundRectPath(ctx, x, y, INNER_W, 44, 10);
          ctx.fill();
          ctx.fillStyle = "#fff";
          ctx.font = shareFont(700, 18);
          ctx.textBaseline = "middle";
          ctx.fillText(medals[i] || `${i + 1}`, x + 14, y + 22);
          ctx.font = shareFont(700, 15);
          const metaStr = `${recordStr} · ${rateStr}`;
          const metaW = ctx.measureText(metaStr).width;
          const nameMax = INNER_W - 14 - 34 - metaW - 14;
          ctx.fillText(ellipsizeText(ctx, r.name, nameMax), x + 48, y + 22);
          ctx.fillStyle = "rgba(255,255,255,0.82)";
          ctx.font = shareFont(500, 13);
          ctx.fillText(metaStr, x + INNER_W - 14 - metaW, y + 22);
        },
      });
      if (i < rows.length - 1) sections.push({ height: 8, draw() {} });
    });
  } else {
    // regular: 3-cell summary grid
    const cells = [
      { num: String(summary.participantCount), lbl: "참여" },
      { num: String(summary.matchCount), lbl: "경기" },
      { num: String(summary.rounds || 0), lbl: "라운드" },
    ];
    const gap = 10;
    const cellW = (INNER_W - gap * (cells.length - 1)) / cells.length;
    sections.push({
      height: 82,
      draw(ctx, x, y) {
        cells.forEach((c, i) => {
          const cx = x + i * (cellW + gap);
          ctx.fillStyle = "rgba(255,255,255,0.08)";
          roundRectPath(ctx, cx, y, cellW, 82, 12);
          ctx.fill();
          ctx.fillStyle = "#fff";
          ctx.font = shareFont(800, 26);
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.fillText(c.num, cx + cellW / 2, y + 30);
          ctx.fillStyle = "rgba(255,255,255,0.75)";
          ctx.font = shareFont(500, 12);
          ctx.fillText(c.lbl, cx + cellW / 2, y + 60);
          ctx.textAlign = "start";
          ctx.textBaseline = "top";
        });
      },
    });
  }

  // ---- Optional full match list ----
  if (showAll) {
    const matches = (detail.matches || []).filter((m: any) => m.winner_side);
    if (matches.length > 0) {
      sections.push({ height: 22, draw() {} });
      sections.push({
        height: 22,
        draw(ctx, x, y) {
          ctx.fillStyle = "rgba(255,255,255,0.06)";
          roundRectPath(ctx, x, y - 4, INNER_W, 1, 0);
          ctx.fill();
          ctx.fillStyle = "rgba(255,255,255,0.82)";
          ctx.font = shareFont(700, 14);
          ctx.textBaseline = "top";
          ctx.fillText("📋 전체 경기 결과", x, y);
        },
      });
      sections.push({ height: 6, draw() {} });

      const byRound = new Map<number, any[]>();
      for (const m of matches) {
        const rn = m.round_number || 0;
        if (!byRound.has(rn)) byRound.set(rn, []);
        byRound.get(rn)!.push(m);
      }
      const sortedRounds = [...byRound.keys()].sort((a, b) => a - b);

      for (const rn of sortedRounds) {
        sections.push({ height: 6, draw() {} });
        sections.push({
          height: 20,
          draw(ctx, x, y) {
            ctx.fillStyle = "rgba(255,255,255,0.6)";
            ctx.font = shareFont(600, 12);
            ctx.textBaseline = "top";
            ctx.fillText(`${rn}라운드`, x, y);
          },
        });
        const ms = byRound.get(rn)!.sort((a, b) => (a.match_order || 0) - (b.match_order || 0));
        for (const m of ms) {
          const sideA = (m.sides || []).find((s: any) => s.side_number === 1);
          const sideB = (m.sides || []).find((s: any) => s.side_number === 2);
          const nameOf = (side: any) =>
            (side?.players || []).map((p: any) => matchPlayerName(p)).join(", ") || "-";
          const nameA = nameOf(sideA);
          const nameB = nameOf(sideB);
          const scoreA = m.side_a_score != null ? String(m.side_a_score) : "-";
          const scoreB = m.side_b_score != null ? String(m.side_b_score) : "-";
          const winA = m.winner_side === "side_a";
          const winB = m.winner_side === "side_b";
          const courtLabel = m.court_label || "";

          // Layout: [court badge] [name ...] [score]
          meas.font = shareFont(700, 13);
          const scoreW = 28;
          const courtW = courtLabel ? Math.min(52, meas.measureText(courtLabel).width + 14) : 0;
          const nameMaxW = INNER_W - 12 - courtW - (courtW ? 8 : 0) - scoreW - 12;

          meas.font = shareFont(600, 13);
          const linesA = wrapTextLines(meas, nameA, nameMaxW, 2);
          const linesB = wrapTextLines(meas, nameB, nameMaxW, 2);

          const ROW_LINE_H = 18;
          const sideAH = Math.max(20, linesA.length * ROW_LINE_H);
          const sideBH = Math.max(20, linesB.length * ROW_LINE_H);
          const cellH = 10 + sideAH + 6 + sideBH + 10;

          sections.push({ height: 6, draw() {} });
          sections.push({
            height: cellH,
            draw(ctx, x, y) {
              ctx.fillStyle = "rgba(255,255,255,0.05)";
              roundRectPath(ctx, x, y, INNER_W, cellH, 10);
              ctx.fill();

              let cx = x + 12;
              if (courtLabel) {
                ctx.fillStyle = "rgba(59,130,246,0.28)";
                roundRectPath(ctx, cx, y + 10, courtW - 2, 22, 6);
                ctx.fill();
                ctx.fillStyle = "#bfdbfe";
                ctx.font = shareFont(700, 11);
                ctx.textBaseline = "middle";
                ctx.textAlign = "center";
                ctx.fillText(courtLabel, cx + (courtW - 2) / 2, y + 21);
                ctx.textAlign = "start";
                cx += courtW + 6;
              }

              const drawSide = (
                lines: string[],
                score: string,
                isWin: boolean,
                topY: number,
                sideH: number,
              ) => {
                ctx.fillStyle = isWin ? "#fff" : "rgba(255,255,255,0.82)";
                ctx.font = shareFont(isWin ? 700 : 600, 13);
                ctx.textBaseline = "top";
                lines.forEach((ln, li) => {
                  ctx.fillText(ln, cx, topY + li * ROW_LINE_H);
                });
                ctx.fillStyle = isWin ? "#fde68a" : "rgba(255,255,255,0.7)";
                ctx.font = shareFont(700, 14);
                ctx.textAlign = "end";
                ctx.textBaseline = "middle";
                ctx.fillText(score, x + INNER_W - 12, topY + sideH / 2);
                ctx.textAlign = "start";
              };
              drawSide(linesA, scoreA, winA, y + 10, sideAH);
              drawSide(linesB, scoreB, winB, y + 10 + sideAH + 6, sideBH);
            },
          });
        }
      }
    }
  }

  // ---- Footer: brand ----
  sections.push({ height: 18, draw() {} });
  sections.push({
    height: 28,
    draw(ctx, x, y) {
      ctx.fillStyle = "rgba(255,255,255,0.12)";
      roundRectPath(ctx, x, y - 4, INNER_W, 1, 0);
      ctx.fill();
      ctx.fillStyle = "#fff";
      ctx.font = shareFont(700, 13);
      ctx.textBaseline = "top";
      ctx.fillText("🎾 페어매치", x, y + 6);
      ctx.fillStyle = "rgba(255,255,255,0.7)";
      ctx.font = shareFont(500, 11);
      const sub = "테니스 클럽 운영 앱";
      const subW = ctx.measureText(sub).width;
      ctx.fillText(sub, x + INNER_W - subW, y + 8);
    },
  });

  // ---- Compute total height ----
  const innerTotal = sections.reduce((acc, s) => acc + s.height, 0);
  const cardH = innerTotal + CARD_PAD_Y * 2;
  const H = PAD_TOP + cardH + PAD_BOT;

  // ---- Create & paint canvas ----
  const canvas = document.createElement("canvas");
  canvas.width = W * DPR;
  canvas.height = H * DPR;
  canvas.style.width = W + "px";
  canvas.style.height = H + "px";
  const ctx = canvas.getContext("2d") as CanvasRenderingContext2D;
  // 클럽 로고 등 이미지 다운스케일 시 부드럽게 — 기본값이 'low'라 명시적으로 high.
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.scale(DPR, DPR);

  // Outer background (solid dark so top/bottom padding doesn't go transparent).
  ctx.fillStyle = "#0f172a";
  ctx.fillRect(0, 0, W, H);

  // Card background with diagonal gradient.
  const cardX = PAD_SIDE;
  const cardY = PAD_TOP;
  const grad = ctx.createLinearGradient(cardX, cardY, cardX + CARD_W, cardY + cardH);
  grad.addColorStop(0, "#0f172a");
  grad.addColorStop(0.55, "#1e3a8a");
  grad.addColorStop(1, "#16a34a");
  ctx.fillStyle = grad;
  roundRectPath(ctx, cardX, cardY, CARD_W, cardH, 22);
  ctx.fill();

  // Subtle inner border.
  ctx.strokeStyle = "rgba(255,255,255,0.08)";
  ctx.lineWidth = 1;
  roundRectPath(ctx, cardX + 0.5, cardY + 0.5, CARD_W - 1, cardH - 1, 21.5);
  ctx.stroke();

  // Paint sections sequentially.
  const innerX = cardX + CARD_PAD_X;
  let cursorY = cardY + CARD_PAD_Y;
  for (const s of sections) {
    s.draw(ctx, innerX, cursorY);
    cursorY += s.height;
  }

  return canvas;
}

function downloadBlob(blob: Blob, fname: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fname;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  toast("이미지를 저장했어요");
}

function ShareSheet({ detail }: { detail: any }) {
  const summary = useState(() => computeShareSummary(detail))[0];
  const [showAll, setShowAll] = useState(false);
  const [status, setStatus] = useState("이미지 생성 중…");
  const [blob, setBlob] = useState<Blob | null>(null);
  const previewRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    setBlob(null);
    setStatus("이미지 생성 중…");
    if (previewRef.current) previewRef.current.innerHTML = "";

    (async () => {
      try {
        const canvas = await drawShareCanvas(detail, summary, { showAll });
        if (cancelled) return;
        if (previewRef.current) {
          previewRef.current.innerHTML = "";
          previewRef.current.append(canvas);
        }
        const b = await new Promise<Blob | null>((res) =>
          canvas.toBlob((bb) => res(bb), "image/png"),
        );
        if (cancelled) return;
        setBlob(b);
        setStatus(navigator.canShare ? "공유하거나 저장할 수 있어요." : "이미지로 저장할 수 있어요.");
      } catch (e) {
        if (cancelled) return;
        console.warn("share render failed", e);
        setStatus("이미지 생성에 실패했어요. 스크린샷으로 공유해주세요.");
      }
    })();

    return () => { cancelled = true; };
  }, [showAll]);

  const fname = `result-${detail.event?.event_id || "event"}.png`;

  async function onShare() {
    if (!blob) return;
    const file = new File([blob], fname, { type: "image/png" });
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({ files: [file], title: detail.event?.title || "일정 결과" });
        // 공유 완료 후 시트 자동 닫기 — 사용자가 다시 시트로 돌아올 일 없음.
        // navigator.share는 사용자가 취소하면 reject하므로 catch에서는 닫지 않음.
        closeSheet();
      } catch { /* user canceled */ }
    } else {
      // share API 미지원 fallback — 다운로드도 공유 완료로 간주.
      downloadBlob(blob, fname);
      closeSheet();
    }
  }

  return (
    <div class="stack">
      <div class="share-preview" ref={previewRef} />
      <label class="share-toggle">
        <input
          type="checkbox"
          checked={showAll}
          onChange={(e) => setShowAll((e.currentTarget as HTMLInputElement).checked)}
        />
        <span>전체 경기 결과 포함 (길이가 많이 길어져요)</span>
      </label>
      <p class="muted small">{status}</p>
      <button
        class="btn btn--primary btn--block"
        type="button"
        disabled={!blob}
        onClick={onShare}
      >
        <Icon name="share" size={16} />
        공유하기
      </button>
      <button
        class="btn btn--secondary btn--block"
        type="button"
        disabled={!blob}
        style="margin-top:8px"
        onClick={() => {
          if (!blob) return;
          downloadBlob(blob, fname);
          closeSheet();
        }}
      >
        <Icon name="download" size={16} />
        이미지 저장
      </button>
    </div>
  );
}

export async function openResultShareSheet(detail: any): Promise<void> {
  openSheet("결과 공유", mountPreact(<ShareSheet detail={detail} />));
}
